import {Alert, Button, Collapse} from 'antd';
import {FC} from 'react';
import {KeyedMutator} from 'swr';
import {IComponent, IDiary, IKey, IKeyMethods} from '../../apis/diaries/diaries';
import {Meta as mainMeta} from './main';

interface IComponentOptions {
  editable: boolean,
}

export interface IComponentProps extends IComponentOptions {
  diary: IDiary,
  comp: IComponent,

  mutate: KeyedMutator<IDiary>;
}

export type ComponentFC = FC<IComponentProps>

export interface IComponentMeta {
  Kind: string,
  Version: number,
  Comp: ComponentFC,

  DefaultContent: (() => string), // default value when create new component or new diary
  CreateWhenCreateDiary: boolean, // create component when create diary
}

const compMetas: IComponentMeta[] = [
  mainMeta,
];
const compSet = new Set(compMetas.map((meta) => meta.Kind));

// new diary to create
export function newDiary(key: IKey) :IDiary {
  const diary :IDiary = {
    Key: key,
    Revision: 0,
    Components: {},
  };
  compMetas.forEach((meta) => {
    if (!meta.CreateWhenCreateDiary) return;
      diary.Components![meta.Kind] = {
        Version: meta.Version,
        // eslint-disable-next-line new-cap
        Content: meta.DefaultContent(),
      };
  });
  return diary;
}

const defaultComponentOptions: IComponentOptions = {
  editable: true,
};

type ComponentFCWrapper = (meta: IComponentMeta, Comp: ComponentFC) => ComponentFC
function wrap(meta: IComponentMeta, Comp: ComponentFC, warppers: ComponentFCWrapper[]): ComponentFC {
  warppers.forEach((w) => {
    Comp = w(meta, Comp);
  });
  return Comp;
}

function checkVersion(meta: IComponentMeta, Comp: ComponentFC): ComponentFC {
  return (props) => {
    const p = {...props};
    p.editable = props.editable && (props.comp.Version === meta.Version);
    return <>  { // allert if data version > web version
      props.comp.Version > meta.Version ?
        <Alert
          showIcon
          type='warning'
          message="WARN"
          description={`component ${meta.Kind} data version(${props.comp.Version}) newer than web version(${meta.Version})`}
        /> :
        null
    }
    <Comp {...p} />
    </>;
  };
}

const weekdaysText = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const Components: FC<{
  diary: IDiary,
  mutate: KeyedMutator<IDiary>,
  options?: IComponentOptions,
}> = ({diary, mutate, options}) => {
  const comps: { kind: string, FC: FC }[] = []; // Collapse.Panel
  const combinedOptions = {...defaultComponentOptions, ...options};
  compMetas.forEach((meta) => {
    const comp = diary.Components?.[meta.Kind];
    comps.push({
      kind: meta.Kind,
      FC: comp ?
        () => {
          const Comp = wrap(meta, meta.Comp, [checkVersion]);
          return <Comp diary={diary} comp={comp} mutate={mutate} {...combinedOptions} />;
        } :
        () => {
          return <Button>Create</Button>;
        },
    });
  });
  Object.keys(diary.Components ? diary.Components : {}).forEach((kind) => {
    if (compSet.has(kind)) {
      return; // already handled, continue
    }
    comps.push({
      kind: kind,
      FC: () => {
        // unknown component kind
        return <Alert
          showIcon
          type='error'
          message="unknown component"
          description={`unrecognized component: ${kind}`}
        />;
      },
    });
  });

  const headerText = (()=>{
    const [y, m, d] = IKeyMethods.asStrings(diary.Key);
    const ymd = `${y}-${m}-${d}`;
    return `${ymd} ${weekdaysText[new Date(ymd).getDay()]}`;
  })();

  return <>
    <p>{headerText}</p>
    <Collapse defaultActiveKey={comps.map(({kind}) => kind)} ghost >
      {comps.map(({kind, FC}) => {
        return <Collapse.Panel key={kind} header={`Component: ${kind}`}>
          <FC />
        </Collapse.Panel>;
      })}
    </Collapse >
  </>;
};
